import React, { useState, useContext } from 'react';
import { AppContext } from 'context/app.context';
import { IonGrid, IonRow, IonCol, IonItem, IonButtons, IonButton, IonCard, IonCardHeader, IonCardTitle, IonIcon, IonCardContent, IonLabel, IonInput, IonReorderGroup, IonReorder, IonText, IonToggle } from '@ionic/react';
import DragList from '../drag/DragList';
import { trash, appsOutline, createOutline } from 'ionicons/icons';
import Droplist from '../drag/DropList';
import PHQProperties from './PHQProperties';
import { Question, Section, QuestionOption } from 'models/questionnaire.model';
import { updateSection, deleteQuestion, deleteSection, reorderSections, reorderQuestions, createQuestion, updateQuestion } from 'misc/phqHTTP';
import { QuestionnaireActionTypes } from 'reducers/questionnaire.reducer';
import '../questionnaire/PHQSection.css';
import FadeIn from '../animations/fadeIn';
import { HttpStatusCodeEnum } from 'models/http-status-code.enum';
import _inRange from 'lodash-es/inRange';
import ConditionalLogicButton from './PHQConditions/ConditionalLogicButton';
import RemoveAlert from '../UI/RemoveAlert';
import { UIElementEnum } from 'models/ui-element.enum';

type ReorderState = { [x:number]: boolean };

const PHQSection = (props: {
    createSection: () => void
}) => {
    const { lookup, questionnaire, updateQuestionnaire, setIsLoading, notifyError, helpMode } = useContext(AppContext);
    const [selectedItem, setSelectedItem] = useState<Question|null>(null);
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [selectedSection, setSelectedSection] = useState<Section | null>(null);
    const reorderStateValues = questionnaire?.PageSelected?.Sections?.reduce((acc: ReorderState, val: Section) => {
        acc[val.SectionID] = false;
        return acc;
    }, {}) || {};
    const [reorderState, setReorderState] = useState<ReorderState>(reorderStateValues);
    const isViewMode = questionnaire?.ViewMode;

    const onReorderStateChange = (section: Section, e: any) => {
        setReorderState({...reorderState, ...{ [section.SectionID]: !!e.detail.checked }})
    }

    const setItemDropped = (item: any, type: any, sectionID: number) => {

        const questionToAdd = new Question();
        questionToAdd.SectionID = sectionID;

        if(type === 'procattrib' || type === 'custom') {
            const procAttrib = lookup?.ProcAttribTypes?.find(x => x.ProcAttribTypeID === item.ProcAttribTypeID) || {};
            questionToAdd.Meta = JSON.stringify({ "Table": "ProcAttribType", "Schema": procAttrib } );
            questionToAdd.Name = item?.Name || 'New ProcAttrib';
            questionToAdd.Label = item?.Label || 'New Question';
            questionToAdd.ProcAttribID = item.ProcAttribTypeID;
            questionToAdd.UIElementID = isNaN(item.UIControlType) ? +UIElementEnum[item.UIControlType] : item.UIControlType;

            //if it's placeholder hide it
            if(questionToAdd.UIElementID === UIElementEnum.ph) {
                questionToAdd.IsLabelHidden = true;
            }

            const options = lookup.ProcAttribOptions?.filter(f => f.ProcAttribTypeID === item.ProcAttribTypeID);
            if (options && options?.length > 0) {
                questionToAdd.QuestionOptions = options.map(o => {
                    const questionOption = new QuestionOption();
                    questionOption.Label = o.Description;
                    questionOption.Name = o.Value
                    questionOption.Priority = o.Priority
                    return questionOption
                })
            }
        }

        if(type === 'healthissue') {
            const healthIssue = lookup?.HealthIssueTypes?.find(x => x.HealthIssueTypeID === item.HealthIssueTypeID) || {};
            questionToAdd.Meta = JSON.stringify({ "Table": "HealthIssueType", "Schema": healthIssue });
            questionToAdd.Label = item?.Description || 'New HealthIssue';
            questionToAdd.Name = item?.Description || "Description";
            questionToAdd.UIElementID = UIElementEnum.cb;
        }

        if (item?.Label === 'Yes/No') {
            const yesQuestionOption = new QuestionOption();
            yesQuestionOption.Label = 'YES'
            yesQuestionOption.Name = 'YES'
            yesQuestionOption.Priority = 10
            const noQuestionOption = new QuestionOption();
            noQuestionOption.Label = 'NO'
            noQuestionOption.Name = 'NO'
            noQuestionOption.Priority = 20
            questionToAdd.QuestionOptions = [yesQuestionOption, noQuestionOption]
        }

        const section = questionnaire.PageSelected?.Sections?.find(s => s.SectionID === sectionID);

        if (section) {
            questionToAdd.Priority = ((section.Questions?.length || 0) + 1) * 10;
        }
        
        createQuestion(questionToAdd).then(res => {
            if (res && res.data && res.data.result) {
                const payload = {
                    sectionItem: res.data.result,
                    type: type,
                    sectionID: sectionID
                }
                updateQuestionnaire(QuestionnaireActionTypes.ADD_ITEM_TO_SECTION, payload);
                if(questionToAdd.UIElementID !== UIElementEnum.ph) {
                    setSelectedItem(res.data.result);
                    setShowToolbox(false);
                }
            }
        })

    }

    const itemSelected = (item: Question) => {
        if(item.UIElementID === UIElementEnum.ph) {
            return;
        }

        setSelectedItem(null);
        setTimeout(() => {
            setSelectedItem(item);
            setShowToolbox(false);
        }); 
    }

    const callDeleteSection = () => {
        setIsLoading(true);
        deleteSection(selectedSection!.SectionID)
        .then(res => {
            if (res.status === HttpStatusCodeEnum.OK) {
                updateQuestionnaire(QuestionnaireActionTypes.DELETE_SECTION, selectedSection!.SectionID);
                setSelectedItem(null);
            }
            else {
                notifyError(res.data.message);
            }
        })
        .finally(() => setIsLoading(false))
    }

    const deleteItem = (question: Question, sectionId: number) => {
        deleteQuestion(question.QuestionID).then(res => {
            if (res.status === HttpStatusCodeEnum.OK) {
                const payload = {
                    question,
                    sectionId
                }
                updateQuestionnaire(QuestionnaireActionTypes.DELETE_QUESTION, payload);
                setShowToolbox(true);
                setSelectedItem(null);
            }
            else {
                notifyError(res?.data?.message);
            }
        })
    }

    const moveQuestion = async (event:any, sectionID: number) => {
        event.stopPropagation();
        event.detail.complete();

        const pageSelected = questionnaire.PageSelected;
        const sections = pageSelected.Sections;
        const questions = sections?.find(s => s.SectionID === sectionID)?.Questions;
        if(questions) {
            const itemMove = questions?.splice(event.detail.from, 1)[0];
            if (itemMove) { questions?.splice(event.detail.to, 0, itemMove); }

            if(questions?.length) {
                for (let index = 0; index < questions.length; index++) {
                    const question = questions[index];
                    question.Priority = (index + 1) * 10;
                }
            }

            setTimeout(() => {
                reorderQuestions(questions)
                updateQuestionnaire(QuestionnaireActionTypes.UPDATE_PAGE, questionnaire.PageSelected);
            });
        }
    }

    const updateColNumber = async (value: number, section: Section) => {

        if(section.Cols !== value) {
            setIsLoading(true);

            section.Cols = value;
            await updateSection(section);

            const promises = section.Questions
                .filter(q => q.ColSpan && q.ColSpan !== value)
                .map(q => {
                    q.ColSpan = null; //null means default/follows section
                    return updateQuestion(q);
            });

            await Promise.all(promises);

            setIsLoading(false);

            updateQuestionnaire(QuestionnaireActionTypes.UPDATE_PAGE, questionnaire.PageSelected);
        }
    }

    const updateSectionInfo = (e: any, section: any) => {
        const propName = e.target.id;
        const numVal = Number(e.target.value);
        let newVal = null;

        if (isNaN(numVal)) {
            newVal = e.target.value;
        }
        else {
            newVal = _inRange(numVal, 1, 7) ? numVal : 1;
        }
        
        if (section[propName] !== newVal) {
            section[propName] = newVal;
            updateSection(section).then(res => {
                if (res.status === HttpStatusCodeEnum.OK) {
                    const payload = {
                        sectionID: section.SectionID,
                        value: section
                    }

                    updateQuestionnaire(QuestionnaireActionTypes.UPDATE_SECTION, payload);
                }
            })
        }
    }

    const setEdit = (section: Section) => {
        section.EditSection = !section.EditSection
        const payload = {
            sectionID: section.SectionID,
            value: section
        }
        updateQuestionnaire(QuestionnaireActionTypes.UPDATE_SECTION, payload);
    }

    const doReoderSections = async (event: any) => {
        if (isViewMode) {
            event.detail.complete();
            return
        }

        const pageSelected = questionnaire.PageSelected;
        const sections = pageSelected.Sections;
        const itemMove = sections?.splice(event.detail.from, 1)[0];
        if (itemMove) { sections?.splice(event.detail.to, 0, itemMove); }

        for (let index = 0; index < sections?.length!; index++) {
            if (sections) {
                const section = sections[index];
                section.Priority = (index + 1) * 10;
            }
        }

        reorderSections(sections!).then(_ => {
            // updateQuestionnaire(QuestionnaireActionTypes.UPDATE_PAGE, pageSelected);
        })

        event.detail.complete();
    }

    const editInfoSection = (section: Section) => {
        return (
            <IonGrid>
                <IonRow>
                    {
                        helpMode && 
                        <IonCol size="2">
                            <IonItem lines="none">
                                <IonLabel><b>ID: </b>{section.SectionID}</IonLabel>
                            </IonItem>
                        </IonCol>
                    }
                    <IonCol size="8">
                        <IonItem lines="none">
                            <IonLabel><b>Label:</b></IonLabel>
                            <IonInput id="Label" onIonBlur={(e) => { updateSectionInfo(e, section) }} value={section.Label}></IonInput>
                        </IonItem>
                    </IonCol>
                    <IonCol>
                        <IonItem lines="none">
                            <IonLabel><b>Cols:</b></IonLabel>
                            <IonInput id="Cols" min="1" max="6" type="number" onIonBlur={(e:any) => { updateColNumber(+e.target.value, section) }} value={section.Cols}></IonInput>
                        </IonItem>
                    </IonCol>
                </IonRow>
            </IonGrid>
        )
    }

    const [showToolbox, setShowToolbox] = useState(true);

    return (
        <div>
            <div className="tab-btns">
                <IonButton expand="full" className={`${showToolbox ? 'font-weight-bold':''}`} onClick={_ => setShowToolbox(true)}>Toolbox</IonButton>
                <IonButton expand="full" className={`${selectedItem && !showToolbox ? 'font-weight-bold':''}`} onClick={_ => setShowToolbox(false)} disabled={!(!!selectedItem)}>Properties</IonButton>
            </div>
            <IonGrid>
                <IonRow>
                    <IonCol size="3" className="ion-no-padding pl-4">
                        {
                            showToolbox && <DragList />
                        }
                        {
                            selectedItem && !showToolbox && <PHQProperties selectedItem={selectedItem} />
                        }
                    </IonCol>
                    <IonCol className="ion-no-padding">
                        {questionnaire?.PageSelected?.Sections?.length === 0 && 
                        <div id="noSections">
                            <IonGrid>
                                <IonRow>
                                    <IonCol className="ion-text-center">
                                        No Data
                                    </IonCol>
                                </IonRow>
                                <IonRow>
                                    <IonCol className="ion-text-center">
                                        <IonButton disabled={isViewMode} color="primary" onClick={() => {
                                            props.createSection()
                                        }} fill="outline"> New Section
                                        </IonButton>
                                    </IonCol>
                                </IonRow>
                            </IonGrid>
                        </div>}
                        <div id="sectionContainer" className='section-container'>
                                <IonReorderGroup key="reorderSection" disabled={false} onIonItemReorder={doReoderSections}>
                                    {
                                        questionnaire?.PageSelected?.Sections?.map((section, index) => (
                                            <FadeIn key={index}>
                                                <IonCard className="section-card">
                                                    <IonCardHeader className='section-header-container'>
                                                        <IonGrid className='ion-no-padding'>
                                                            <IonRow className="ion-no-padding">
                                                                <IonCol size="6" className="ion-no-padding pointer">
                                                                    <IonItem onClick={(event) => { setEdit(section); event.stopPropagation() }} disabled={isViewMode} lines='none'>
                                                                        <IonReorder slot="start">
                                                                            <IonIcon icon={appsOutline} />
                                                                        </IonReorder>
                                                                        <IonCardTitle className='section-label ion-text-wrap'>
                                                                            <IonText>{section.Label}</IonText>
                                                                            <IonIcon style={{verticalAlign: 'bottom'}} className="ml-1" icon={createOutline} color="primary"></IonIcon>
                                                                        </IonCardTitle>
                                                                    </IonItem>
                                                                </IonCol>
                                                                <IonCol size="6" className="my-auto">
                                                                    <IonButtons className='ion-float-right'>
                                                                        <IonItem lines="none" hidden={!section.Questions?.length || !section.EditSection}>
                                                                            <IonLabel>
                                                                                <IonText color="primary"><b>Reorder?</b></IonText>
                                                                            </IonLabel>
                                                                            <IonToggle style={{height: '28px'}} disabled={isViewMode} checked={reorderState[section.SectionID]} onIonChange={e => onReorderStateChange(section, e) }></IonToggle>
                                                                        </IonItem>
                                                                        <ConditionalLogicButton section={section}></ConditionalLogicButton>
                                                                        <IonButton disabled={isViewMode} className="ipro-btn" color="danger" fill='clear' onClick={(event) => { setSelectedSection(section); setShowDeleteAlert(true); event.stopPropagation() }}>
                                                                            <IonIcon slot="icon-only" icon={trash} />
                                                                        </IonButton>
                                                                    </IonButtons>
                                                                </IonCol>
                                                            </IonRow>
                                                        </IonGrid>
                                                    </IonCardHeader>
                                                    <IonCardContent className={`section-items-container ${!section.EditSection && 'p-1'}`}>
                                                        {section.EditSection && (
                                                        <div> 
                                                            {editInfoSection(section)} 
                                                            <Droplist 
                                                                key={index}
                                                                cols={section.Cols}
                                                                items={section.Questions || []}
                                                                reorder={section.IsReorder || reorderState[section.SectionID]}
                                                                sectionID={section.SectionID}
                                                                onMoveItem={moveQuestion}
                                                                onItemDeleted={deleteItem}
                                                                onItemDropped={setItemDropped}
                                                                onItemClicked={itemSelected} />
                                                       </div> )}
                                                    </IonCardContent>
                                                </IonCard>
                                            </FadeIn>
                                        ))
                                    }
                                </IonReorderGroup>
                        </div>
                    </IonCol>
                </IonRow>
            </IonGrid>
            <RemoveAlert isOpen={showDeleteAlert} onCancel={() => setShowDeleteAlert(false)} onDelete={() => callDeleteSection()} />
        </div>
    );
};

export default PHQSection;
